import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useClinic } from "@/contexts/ClinicContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Calendar, Users, UserCircle, Clipboard, Bell, Building2,
  Hospital, Inbox, FileUp, CalendarOff, LucideIcon,
  CheckCircle2, Clock, XCircle, UserX, ArrowRight, MessageCircle,
  Activity,
} from "lucide-react";
import { fetchAppointments, AppointmentData } from "@/services/appointment-service";
import { fetchPendingTickets } from "@/services/chat-service";
import { fetchNotifications } from "@/services/notification-center-service";
import { cn } from "@/lib/utils";

type HomeItem = {
  title: string;
  description: string;
  icon: LucideIcon;
  path: string;
  /** Permission exigida (resource, action). Omitido = visível pra qualquer autenticado. */
  requires?: [string, string];
};

const allItems: HomeItem[] = [
  { title: "Agenda",                  description: "Agende consultas, remarque e retorno para os pacientes.",       icon: Calendar,    path: "/schedule",             requires: ["appointments", "read"] },
  { title: "Caixa de Mensagens",      description: "Conversas via WhatsApp que aguardam resposta da equipe.",       icon: Inbox,       path: "/chat-observer",        requires: ["chat_tickets", "read"] },
  { title: "Central de Notificações", description: "Gerencie lembretes e comunicações com pacientes.",              icon: Bell,        path: "/notifications",        requires: ["notifications", "read"] },
  { title: "Atendimentos",            description: "Faça a gestão dos seus atendimentos.",                          icon: Clipboard,   path: "/appointments",         requires: ["appointments", "read"] },
  { title: "Pacientes",               description: "Cadastre seus pacientes.",                                      icon: Users,       path: "/patients",             requires: ["patients", "read"] },
  { title: "Expedientes",             description: "Configure suas agendas.",                                       icon: Activity,    path: "/em-implementacao",     requires: ["schedule_setups", "read"] },
  { title: "Feriados",                description: "Cadastre os feriados que afetam a agenda da clínica.",          icon: CalendarOff, path: "/holidays",             requires: ["holidays", "read"] },
  { title: "Importar Atendimentos",   description: "Importe planilhas de atendimentos para o sistema.",             icon: FileUp,      path: "/import-appointments",  requires: ["appointments", "import_bulk"] },
  { title: "Profissionais",           description: "Cadastre novos profissionais e acesse o perfil de cada um.",    icon: UserCircle,  path: "/dentists",             requires: ["dentists", "read"] },
  { title: "Clínicas",                description: "Cadastre e gerencie as clínicas odontológicas.",                icon: Hospital,    path: "/clinics-detail",       requires: ["clinics", "read"] },
  { title: "Empresas",                description: "Gerencie as empresas cadastradas no sistema.",                  icon: Building2,   path: "/companies",            requires: ["companies", "read"] },
  { title: "Usuários",                description: "Cadastre os usuários e configure suas permissões.",             icon: Users,       path: "/users",                requires: ["users", "read"] },
];

const STATUS_CFG: Record<string, { label: string; cls: string; icon: LucideIcon }> = {
  booked:      { label: "Agendada",         cls: "bg-blue-100 text-blue-700 border-blue-200",       icon: Calendar },
  confirmed:   { label: "Confirmada",       cls: "bg-green-100 text-green-700 border-green-200",    icon: CheckCircle2 },
  attended:    { label: "Atendida",         cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  arrived:     { label: "Chegou",           cls: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock },
  in_progress: { label: "Em atendimento",   cls: "bg-purple-100 text-purple-700 border-purple-200", icon: Clock },
  no_show:     { label: "Faltou",           cls: "bg-red-100 text-red-700 border-red-200",          icon: UserX },
  not_attended:{ label: "Faltou",           cls: "bg-red-100 text-red-700 border-red-200",          icon: UserX },
  canceled:    { label: "Cancelada",        cls: "bg-gray-100 text-gray-400 border-gray-200",       icon: XCircle },
  rescheduled: { label: "Remarcada",        cls: "bg-gray-100 text-gray-400 border-gray-200",       icon: XCircle },
};

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function StatCard({ icon: Icon, label, value, sub, color, onClick, loading }: {
  icon: LucideIcon; label: string; value: number | string; sub?: string;
  color: string; onClick?: () => void; loading?: boolean;
}) {
  return (
    <Card
      className={cn("border transition-all", onClick && "cursor-pointer hover:shadow-md hover:border-primary/40")}
      onClick={onClick}
    >
      <CardContent className="pt-4 pb-4 px-4">
        <div className="flex items-start justify-between gap-3">
          <div className={cn("p-2 rounded-lg shrink-0", color)}>
            <Icon className="h-4 w-4" />
          </div>
          {loading ? (
            <Skeleton className="h-8 w-12 mt-0.5" />
          ) : (
            <span className="text-2xl font-bold font-display tabular-nums leading-none mt-1">{value}</span>
          )}
        </div>
        <p className="text-xs font-medium text-foreground mt-2">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Home() {
  const { user, can } = useAuth();
  const { selectedClinicId } = useClinic();
  const navigate = useNavigate();

  // "Operacional" = quem vê agenda/atendimentos do dia (pode ser receptionist, clinic_manager, etc)
  const isOperational = can("appointments", "read");

  const menuItems = allItems.filter(i => !i.requires || can(i.requires[0], i.requires[1]));

  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [pendingMessages, setPendingMessages] = useState(0);
  const [pendingNotifications, setPendingNotifications] = useState(0);
  const [loadingDash, setLoadingDash] = useState(false);

  useEffect(() => {
    if (!isOperational || !selectedClinicId) {
      setAppointments([]);
      setPendingMessages(0);
      setPendingNotifications(0);
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    setLoadingDash(true);
    Promise.allSettled([
      fetchAppointments(false, false, selectedClinicId, undefined, undefined, undefined, undefined, today, today, undefined, undefined, undefined, undefined, undefined, 1, 200),
      fetchPendingTickets(selectedClinicId),
      fetchNotifications({ clinicId: selectedClinicId, status: "pending", pageSize: 100, page: 1 }),
    ]).then(([apptRes, ticketsRes, notifRes]) => {
      if (apptRes.status === "fulfilled") setAppointments(apptRes.value);
      if (ticketsRes.status === "fulfilled") setPendingMessages(ticketsRes.value.length);
      if (notifRes.status === "fulfilled") setPendingNotifications(notifRes.value.length);
    }).finally(() => setLoadingDash(false));
  }, [selectedClinicId, isOperational]);

  // Breakdown de status
  const active   = appointments.filter(a => ["booked","confirmed","arrived","in_progress"].includes(a.appointment_status)).length;
  const attended = appointments.filter(a => a.appointment_status === "attended").length;
  const missed   = appointments.filter(a => ["no_show","not_attended"].includes(a.appointment_status)).length;
  const canceled = appointments.filter(a => ["canceled","rescheduled"].includes(a.appointment_status)).length;

  // Próximas consultas do dia (não atendidas/canceladas), ordenadas por horário
  const upcoming = appointments
    .filter(a => ["booked","confirmed","arrived","in_progress"].includes(a.appointment_status))
    .sort((a, b) => (a.slot_time ?? "").localeCompare(b.slot_time ?? ""))
    .slice(0, 8);

  const firstName = (user?.name || "").split(" ")[0] || "Usuário";
  const totalToday = appointments.filter(a => !["rescheduled","unbooked"].includes(a.appointment_status)).length;

  return (
    <div className="px-4 py-4 space-y-5">

      {/* Saudação */}
      <div>
        <h1 className="text-2xl font-bold text-foreground font-display">
          {getGreeting()}, {firstName}!
        </h1>
        {isOperational && selectedClinicId && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {loadingDash
              ? "Carregando resumo do dia..."
              : totalToday > 0
                ? `${totalToday} consulta${totalToday !== 1 ? "s" : ""} hoje · ${active} pendente${active !== 1 ? "s" : ""} · ${attended} atendida${attended !== 1 ? "s" : ""}`
                : "Nenhuma consulta agendada para hoje."}
          </p>
        )}
        {isOperational && !selectedClinicId && (
          <p className="text-xs text-muted-foreground mt-0.5">Selecione uma clínica para ver o resumo do dia.</p>
        )}
        {!isOperational && (
          <p className="text-xs text-muted-foreground mt-0.5">O que você quer fazer hoje?</p>
        )}
      </div>

      {/* Widgets de stats — só para admin_local e user com clínica selecionada */}
      {isOperational && selectedClinicId && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Calendar}       label="Consultas hoje"     value={loadingDash ? "-" : totalToday}            sub={`${active} pend. · ${attended} atend.`}  color="bg-primary/10 text-primary"         loading={loadingDash} />
            <StatCard icon={CheckCircle2}   label="Atendidas"          value={loadingDash ? "-" : attended}              sub={missed > 0 ? `${missed} falta${missed !== 1 ? "s" : ""}` : "Sem faltas"} color="bg-emerald-100 text-emerald-700" loading={loadingDash} />
            <StatCard icon={MessageCircle}  label="Mensagens pend."    value={loadingDash ? "-" : pendingMessages}       sub="Aguardando resposta"                      color="bg-orange-100 text-orange-700"      loading={loadingDash} onClick={() => navigate("/chat-observer")} />
            <StatCard icon={Bell}           label="Notif. pendentes"   value={loadingDash ? "-" : pendingNotifications}  sub="Na fila de envio"                         color="bg-violet-100 text-violet-700"      loading={loadingDash} onClick={() => navigate("/notifications")} />
          </div>

          {/* Próximas consultas do dia */}
          {(upcoming.length > 0 || loadingDash) && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Próximas consultas hoje
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground" onClick={() => navigate("/schedule")}>
                    Ver agenda <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                {loadingDash ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full rounded" />)}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {upcoming.map(apt => {
                      const cfg = STATUS_CFG[apt.appointment_status] ?? STATUS_CFG.booked;
                      const Icon = cfg.icon;
                      return (
                        <div key={apt.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-muted/40 transition-colors">
                          <span className="text-xs font-mono font-semibold text-muted-foreground w-10 shrink-0">
                            {apt.slot_time?.slice(0, 5) ?? "--:--"}
                          </span>
                          <span className="text-xs font-medium flex-1 truncate">{apt.patient_name}</span>
                          <span className="text-[10px] text-muted-foreground truncate hidden sm:block max-w-[120px]">{apt.dentist_name}</span>
                          <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border shrink-0", cfg.cls)}>
                            <Icon className="h-2.5 w-2.5" />
                            {cfg.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Cards de navegação */}
      <div>
        {isOperational && <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Acesso rápido</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {menuItems.map((item) => (
            <Card
              key={item.title}
              className="cursor-pointer transition-all duration-200 hover:border-primary/50 hover:shadow-md border-border bg-card"
              onClick={() => navigate(item.path)}
            >
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">{item.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
